@use "../../../../scss/settings/colors";
@use "../../../../scss/settings/breakpoints";

.page_title {
  text-transform: capitalize;
  color: colors.$base-black;
  font-size: 26px;
}

.page_description {
  margin-bottom: 16px;
  font-family: 'Medium Inter', sans-serif;
}

.document_header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;

  .actions_group {
    display: flex;
    align-items: center;
    gap: 16px;
    transform: translateY(8px);
  }
}

.upload_container {
  position: relative;
  width: fit-content;

  .upload_form {
    position: absolute;
    top: 0;
    left: 100%;
    margin-left: 8px;
    z-index: 1000;
    background-color: colors.$base-white;
    width: 430px;
    height: 165px;
  }
}

.form_box {
  position: absolute;
  z-index: 1005;
  animation: pulse 0.5s ease-in-out;
  border-color: colors.$brand-purple;
  top: 4rem;
}

.form_upload{
  border-color: colors.$brand-purple;
  margin-left: 32px;
}


@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
}

// ---------- Responsive ----------
.form_box {

  @media #{breakpoints.$mobile}{
    position: fixed;
    top: 50%;
    left: 16px;
    right: 16px;
    margin-left: 0;
    max-height: calc(100vh - 32px);
    overflow-y: auto;
    transform: translateY(-50%);
    animation: none;
    z-index: 1300;
  }

}

.form_upload {

  @media #{breakpoints.$mobile}{
    margin-left: 0;
  }

}

.upload_container .upload_form {

  @media #{breakpoints.$mobile}{
    position: fixed;
    top: 50%;
    left: 16px;
    right: 16px;
    width: auto;
    height: auto;
    margin-left: 0;
    transform: translateY(-50%);
    z-index: 1300;
  }

}

.document_header {
  flex-wrap: wrap;
  gap: 8px;
}

// ---------- Layout: aside + documents block (same as the Document Vault page) ----------
$aside-width: 220px;
$block-radius: 16px;
$aside-active-bg: colors.$light-purple;
$control-height: 40px;
$switcher-bg: #eef0f4;

.vault_layout {
  display: flex;
  align-items: flex-start;
  gap: 20px;
}

.aside {
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  gap: 16px;
  width: $aside-width;
  padding: 12px;
  background-color: colors.$base-white;
  border: 1px solid colors.$gray-variant-4;
  border-radius: $block-radius;
}

.aside_divider {
  height: 1px;
  margin: 0 10px;
  background-color: colors.$gray-variant-4;
}

.aside_section {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.aside_label:global(.MuiTypography-root) {
  margin: 0;
  padding: 0 10px;
  font-family: 'SemiBold Inter', sans-serif;
  font-size: 12px !important;
  line-height: 18px;
  color: colors.$gray-variant-16;
}

// Categories: vertical MUI tabs styled as nav items (horizontal on tablet / mobile).
// Scoped under `.aside` so it outranks the global `.updated-variant .MuiTabs-root` rules
// (scss/common/_tab-panel.scss), which fix the scroller / tab height to 38px for horizontal tabs
// and would otherwise clip the vertical list down to its first item.
.aside .category_tabs:global(.MuiTabs-root) {
  min-height: 0;
  margin-bottom: 0;

  :global(.MuiTabs-scroller) {
    height: auto;
  }

  :global(.MuiTabs-flexContainer) {
    gap: 2px;
  }

  :global(.MuiButtonBase-root.MuiTab-root) {
    align-items: flex-start;
    justify-content: center;
    min-width: 0;
    max-width: none;
    height: auto;
    min-height: 36px;
    margin-right: 0;
    padding: 8px 10px;
    font-family: 'Medium Inter', sans-serif;
    font-size: 14px;
    line-height: 20px;
    text-align: left;
    text-transform: none;
    color: colors.$dark-blue;
    border-radius: 8px;
    transition: color 0.2s, background-color 0.2s;

    &:hover {
      color: colors.$brand-purple;
    }

    &:focus-visible {
      outline: 2px solid colors.$brand-purple;
      outline-offset: -2px;
    }

    &:global(.Mui-selected) {
      font-family: 'SemiBold Inter', sans-serif;
      color: colors.$brand-purple;
      background-color: $aside-active-bg;
    }
  }

  // Scroll arrows of the horizontal (tablet / mobile) strip.
  :global(.MuiButtonBase-root.MuiTabScrollButton-root) {
    width: 28px;
    height: auto;
    min-height: 36px;
    margin-right: 0;
    border-radius: 8px;
  }

  // The active item is marked by its background, as in the mockup.
  :global(.MuiTabs-indicator) {
    display: none;
  }
}

// "How Key Dates work": opens the overview tooltip.
.aside_help {
  display: inline-flex;
  align-items: flex-start;
  align-self: flex-start;
  gap: 6px;
  padding: 4px 10px;
  font-family: 'Medium Inter', sans-serif;
  text-align: left;
  font-size: 12px;
  line-height: 18px;
  color: colors.$brand-purple;
  text-decoration: underline;
  text-underline-offset: 2px;
  background: none;
  border: none;
  border-radius: 6px;
  cursor: pointer;

  &:hover {
    text-decoration: none;
  }

  &:focus-visible {
    outline: 2px solid colors.$brand-purple;
    outline-offset: 1px;
  }

  @media #{breakpoints.$mobile}{
    display: none;
  }

}

.aside_help_icon:global(.MuiSvgIcon-root) {
  font-size: 15px;
}

// ---------- Documents block: header (title, pagination, view switcher) + table / cards ----------
.vault_block {
  flex: 1;
  min-width: 0;

  // Card view: the header is a rounded block of its own above the cards.
  .block_header {
    margin-bottom: 15px;
    border: 1px solid colors.$gray-variant-4;
    border-radius: $block-radius;
  }
}

// List view: the header and the table form one rounded block.
.vault_block_joined {
  overflow: hidden;
  background-color: colors.$base-white;
  border: 1px solid colors.$gray-variant-4;
  border-radius: $block-radius;

  .block_header {
    margin-bottom: 0;
    border: none;
    border-bottom: 1px solid colors.$gray-variant-4;
    border-radius: 0;
  }
}

.vault_block_content {
  position: relative;
}

.block_header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 12px 16px;
  background-color: colors.$base-white;
}

.block_title:global(.MuiTypography-root) {
  margin: 0;
  font-family: 'SemiBold Inter', sans-serif;
  font-size: 1rem;
  line-height: 1.5rem;
  color: colors.$dark-blue;
}

// Pagination + view switcher on the right of the header.
.header_controls {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  margin-left: auto;
}

.header_pagination {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.pages_value:global(.MuiTypography-root) {
  font-family: 'Medium Inter', sans-serif;
  font-size: 0.8125rem;
  color: colors.$gray-variant-16;
}

.pagination:global(.MuiPagination-root) {

  :global(.MuiPagination-ul) {
    gap: 4px;
  }

  :global(.MuiPaginationItem-root) {
    min-width: 32px;
    height: 32px;
    margin: 0;
    font-family: 'Medium Inter', sans-serif;
    font-size: 0.8125rem;
    color: colors.$dark-blue;
    border-radius: 8px;

    &:global(.Mui-selected) {
      color: colors.$brand-purple;
      background-color: $aside-active-bg;
    }
  }
}

// Card / list switcher: icon-only segmented control.
.card_list_view_container {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 2px;
  height: $control-height;
  padding: 3px;
  background-color: $switcher-bg;
  border-radius: 10px;
}

.variant_btn {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 34px;
  height: 34px;
  padding: 0;
  color: colors.$dark-blue;
  background-color: transparent;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: color 0.2s, background-color 0.2s, box-shadow 0.2s;

  &[disabled] {
    opacity: 0.5;
  }

  &:hover:not([disabled]) {
    color: colors.$brand-purple;
  }

  &.variant_btn_active:not([disabled]) {
    color: colors.$brand-purple;
    background-color: colors.$base-white;
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.12);
  }

  &:focus-visible {
    outline: 2px solid colors.$brand-purple;
    outline-offset: 1px;
  }

  svg {
    width: 16px;
    height: 16px;
    fill: currentColor !important;
  }
}

.visually_hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
}

// Tablet / mobile: the aside goes above the documents block.
.vault_layout {

  @media #{breakpoints.$tablet-mobile}{
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }

}

.aside {

  @media #{breakpoints.$tablet-mobile}{
    width: 100%;
    gap: 12px;
  }

}

.aside_divider {

  @media #{breakpoints.$tablet-mobile}{
    margin: 0;
  }

}

.aside_label:global(.MuiTypography-root) {

  @media #{breakpoints.$tablet-mobile}{
    display: none;
  }

}

.aside .category_tabs:global(.MuiTabs-root) :global(.MuiButtonBase-root.MuiTab-root) {

  @media #{breakpoints.$tablet-mobile}{
    flex-shrink: 0;
    align-items: center;
    white-space: nowrap;
  }

}

// Mobile: the controls take a line of their own under the title, the switcher stays on the right.
.header_controls {

  @media #{breakpoints.$mobile}{
    justify-content: space-between;
    width: 100%;
    gap: 8px;
  }

}

.header_controls .card_list_view_container {

  @media #{breakpoints.$mobile}{
    margin-left: auto;
  }

}
